import React, { useCallback, useContext, useEffect } from 'react';
import { MapContainer, TileLayer, useMap, Marker } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

import L from 'leaflet';

import { POIVenuesContext, POIMapContext } from 'modules/point-of-interest';

L.Icon.Default.imagePath = 'map/';

const venueIcon = L.icon({
  iconUrl: 'map/markers/map_pin.png',
  iconRetinaUrl: 'map/markers/map_pin@2x.png',
  iconSize: [24, 39],
  iconAnchor: [12, 39],

  shadowUrl: 'map/markers/pin_shadow.png',
  shadowRetinaUrl: 'map/markers/pin_shadow@2x.png',
  shadowSize: [24, 19],
  shadowAnchor: [1, 19],
});

const selectedVenueIcon = L.icon({
  iconUrl: 'map/markers/map_pin_selected.png',
  iconRetinaUrl: 'map/markers/map_pin_selected@2x.png',
  iconSize: [24, 39],
  iconAnchor: [12, 39],

  shadowUrl: 'map/markers/pin_shadow.png',
  shadowRetinaUrl: 'map/markers/pin_shadow@2x.png',
  shadowSize: [24, 19],
  shadowAnchor: [1, 19],
});

const mainMarkerIcon = L.icon({
  iconUrl: 'map/markers/map_point_selector.png',
  iconRetinaUrl: 'map/markers/map_point_selector@2x.png',
  iconSize: [24, 24],
  iconAnchor: [12, 12],

  shadowUrl: 'map/markers/selector_shadow.png',
  shadowRetinaUrl: 'map/markers/selector_shadow@2x.png',
  shadowSize: [24, 24],
  shadowAnchor: [13, 13],
});

const getPositionOfVenue = (venue) => {
  if (!venue) {
    return null;
  }

  const coordinates = venue?.geometry?.coordinates;
  const [lon, lat] = coordinates;

  return [lat, lon];
};

const PanAdjustment = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (!position) {
      return;
    }
    map.panTo(position);
  }, [position, map]);

  return null;
};

const BoundsAdjustment = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (!bounds) {
      return;
    }
    map.fitBounds(bounds, 11);
  }, [bounds, map]);

  return null;
};

const MainMarker = ({ position, icon }) => {
  if (!position) {
    return null;
  }

  return <Marker position={position} icon={icon} />;
};

const VenueMarker = ({ venue, icon, onClick }) => {
  if (!venue) {
    return null;
  }

  const position = getPositionOfVenue(venue);

  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        click: () => {
          onClick(venue?.properties?.gid);
        },
      }}
    />
  );
};

const originalCenter = [51.505, -0.09];
const originalZoom = 1;

const POIMap = ({ className }) => {
  const { venues, selectVenue, selectedVenue } = useContext(POIVenuesContext);
  const { mapBounds, mainMarker } = useContext(POIMapContext);

  const handleVenueClick = useCallback(
    (data) => {
      selectVenue(data);
    },
    [selectVenue]
  );

  const panPosition = getPositionOfVenue(venues?.get(selectedVenue));

  return (
    <MapContainer center={originalCenter} zoom={originalZoom} className={className} id="POIMap">
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <BoundsAdjustment bounds={mapBounds} />
      <MainMarker position={mainMarker} icon={mainMarkerIcon} />
      <PanAdjustment position={panPosition} />
      {Array.from(venues?.keys() || {}).map((id) => (
        <VenueMarker
          venue={venues.get(id)}
          key={id}
          icon={selectedVenue === id ? selectedVenueIcon : venueIcon}
          onClick={handleVenueClick}
        />
      ))}
    </MapContainer>
  );
};

export default POIMap;
